import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Paper, Stack, TextField, Typography } from '@mui/material';
import { CheckCircleOutline, History, Refresh, CancelOutlined, ReplayOutlined } from '@mui/icons-material';
import adminService from '../../services/adminService';

const formatDate = (value) => value ? new Date(value).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const STATUS_META = {
  pending: { label: 'Pending', color: 'warning' },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'error' },
  reupload_required: { label: 'Re-upload Required', color: 'default' }
};

const PhotoVerification = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [mode, setMode] = useState('');
  const [reason, setReason] = useState('');
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const pendingPhotoCount = profiles.reduce(
    (count, profile) => count + (profile.photos || []).filter((photo) => photo.moderationStatus === 'pending').length,
    0
  );

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminService.getPhotoReviewQueue({ page: 1, limit: 100 });
      setProfiles(response.data.profiles || []);
    } catch (error) {
      setNotice({ severity: 'error', text: error.response?.data?.message || 'Failed to load photo review queue.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const startReview = (profile, photo, action) => {
    setSelectedProfile(profile);
    setSelectedPhoto(photo);
    setMode(action);
    setReason('');
  };

  const closeDialog = () => {
    if (saving) return;
    setSelectedProfile(null);
    setSelectedPhoto(null);
    setMode('');
    setHistory([]);
  };

  const review = async () => {
    setSaving(true);
    try {
      await adminService.reviewPhoto(selectedProfile._id, selectedPhoto._id, { action: mode, reason });
      setNotice({ severity: 'success', text: mode === 'approve' ? 'Photo approved.' : mode === 'reject' ? 'Photo rejected.' : 'Re-upload requested.' });
      setSelectedProfile(null);
      setSelectedPhoto(null);
      setMode('');
      await loadQueue();
    } catch (error) {
      setNotice({ severity: 'error', text: error.response?.data?.message || 'Review failed.' });
    } finally {
      setSaving(false);
    }
  };

  const showHistory = async (profile) => {
    setSelectedProfile(profile);
    setMode('history');
    setHistory([]);
    try {
      const response = await adminService.getPhotoVerificationHistory(profile._id);
      setHistory(response.data.profile?.photoVerificationHistory || []);
    } catch (error) {
      setNotice({ severity: 'error', text: error.response?.data?.message || 'History could not be loaded.' });
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' gap={2} mb={3}>
        <Box>
          <Typography variant='h4'>Photo Verification</Typography>
          <Typography color='text.secondary'>Approve profile photos before they become visible to other members.</Typography>
        </Box>
        <Stack direction='row' gap={1} alignItems='center'>
          <Chip color='warning' label={pendingPhotoCount + ' pending'} />
          <Button variant='outlined' startIcon={<Refresh />} onClick={loadQueue}>Refresh</Button>
        </Stack>
      </Stack>
      {notice && <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}
      {loading ? <Box textAlign='center' py={8}><CircularProgress /></Box> : profiles.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <CheckCircleOutline color='success' sx={{ fontSize: 54 }} />
          <Typography variant='h6' mt={1}>Review queue is clear</Typography>
          <Typography color='text.secondary'>No pending photo submissions.</Typography>
        </Paper>
      ) : <Stack gap={2}>{profiles.map((profile) => (
        <Paper key={profile._id} sx={{ p: { xs: 2, md: 3 } }}>
          <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={1} mb={2}>
            <Typography variant='h6'>{profile.user?.name || profile.name || 'Unnamed user'}</Typography>
            <Stack direction='row' gap={1} alignItems='center'>
              <Typography variant='body2' color='text.secondary'>{profile.user?.email || 'No email'}</Typography>
              <Button size='small' startIcon={<History />} onClick={() => showHistory(profile)}>History</Button>
            </Stack>
          </Stack>
          <Grid container spacing={2}>
            {(profile.photos || []).filter((photo) => photo.moderationStatus === 'pending').map((photo) => (
              <Grid item xs={12} sm={6} md={4} key={photo._id}>
                <Paper variant='outlined' sx={{ p: 1.5 }}>
                  <Box
                    component='img'
                    src={photo.url}
                    alt='Submitted profile'
                    sx={{ width: '100%', height: 220, objectFit: 'cover', borderRadius: 1, mb: 1 }}
                  />
                  <Stack direction='row' justifyContent='space-between' alignItems='center' mb={1}>
                    <Chip size='small' color={STATUS_META[photo.moderationStatus]?.color || 'default'} label={STATUS_META[photo.moderationStatus]?.label || photo.moderationStatus} />
                    <Typography variant='caption' color='text.secondary'>{formatDate(photo.uploadDate)}</Typography>
                  </Stack>
                  <Stack gap={1}>
                    <Button size='small' color='success' variant='contained' startIcon={<CheckCircleOutline />} onClick={() => startReview(profile, photo, 'approve')}>Approve</Button>
                    <Button size='small' color='error' variant='outlined' startIcon={<CancelOutlined />} onClick={() => startReview(profile, photo, 'reject')}>Reject</Button>
                    <Button size='small' variant='outlined' startIcon={<ReplayOutlined />} onClick={() => startReview(profile, photo, 'reupload_required')}>Re-upload Required</Button>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      ))}</Stack>}

      <Dialog open={Boolean(selectedPhoto && mode && mode !== 'history')} onClose={closeDialog} fullWidth maxWidth='sm'>
        <DialogTitle>
          {mode === 'reject' ? 'Reject Photo' : mode === 'reupload_required' ? 'Request Re-upload' : 'Approve Photo'}
        </DialogTitle>
        <DialogContent>
          {selectedPhoto && (
            <Box component='img' src={selectedPhoto.url} alt='Submitted profile' sx={{ width: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 1, mt: 1 }} />
          )}
          {(mode === 'reject' || mode === 'reupload_required') && (
            <TextField
              autoFocus
              required
              fullWidth
              multiline
              minRows={3}
              label={mode === 'reject' ? 'Reason for rejection' : 'What needs to change'}
              placeholder='e.g. Not a clear face photo, WhatsApp DP, image of a deity/flower, blurred photo, someone else in the photo'
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant='contained'
            color={mode === 'reject' ? 'error' : mode === 'reupload_required' ? 'warning' : 'success'}
            onClick={review}
            disabled={saving || ((mode === 'reject' || mode === 'reupload_required') && !reason.trim())}
          >
            {saving ? 'Saving…' : mode === 'reject' ? 'Reject' : mode === 'reupload_required' ? 'Request Re-upload' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedProfile && mode === 'history')} onClose={closeDialog} fullWidth maxWidth='md'>
        <DialogTitle>Photo Verification History</DialogTitle>
        <DialogContent>
          {history.length === 0 ? <Typography color='text.secondary' py={3}>No history found.</Typography> : (
            <Stack divider={<Divider flexItem />}>{[...history].reverse().map((entry) => (
              <Box key={entry._id || entry.changedAt} py={2}>
                <Stack direction='row' justifyContent='space-between'>
                  <Chip size='small' label={entry.action.replaceAll('_', ' ')} />
                  <Typography variant='caption'>{formatDate(entry.changedAt)}</Typography>
                </Stack>
                <Typography variant='body2' mt={1}>{'Changed by ' + (entry.actorAdmin?.name || entry.actorUser?.name || entry.actorType)}</Typography>
                {entry.reason && <Alert severity='warning' sx={{ my: 1 }}>{entry.reason}</Alert>}
              </Box>
            ))}</Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={closeDialog}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhotoVerification;
